"use client";

import {
   Sidebar,
   SidebarContent,
   SidebarHeader,
   SidebarMenu,
} from "@/Components/ui/sidebar";
import { useState } from "react";
import { useAdminStore } from "../../../store/useAdminStore";
import { ConfigurableList } from "./ConfigurableList";
import { stageConfig } from "../../../configs/listConfig";
import { Item, MainProduct, Option, Stage } from "@/types/productTypes";

// ------------------------------------------
// ExplorerSidebar (Parent)
// ------------------------------------------
interface ExplorerSidebarProps {}

export function ExplorerSidebar({}: ExplorerSidebarProps) {
   const adminStore = useAdminStore();
   const selectedProductId = adminStore.selectedProductId;

   const product = adminStore.allProducts.find(
      (p) => p.id === adminStore.selectedProductId
   );

   const [expandedState, setExpandedState] = useState<Record<string, boolean>>(
      {}
   );

   if (!product) return null;

   const toggleExpand = (key: string) => {
      setExpandedState((prev) => ({ ...prev, [key]: !prev[key] }));
   };
   type ThingType = "stage" | "option" | "item";
   const siblingAccessors: {
      [K in ThingType]: (
         parent: MainProduct | Stage | Option
      ) => { id?: number; relativePos: number }[] | undefined;
   } = {
      stage: (parent) => (parent as MainProduct).stages,
      option: (parent) => (parent as Stage).options,
      item: (parent) => (parent as Option).items,
   };

   const moveUp = (
      index: number,
      upperThing: MainProduct | Stage | Option,
      type: ThingType
   ): number => {
      if (index === 0) return -1;
      if (index > (siblingAccessors[type](upperThing)?.length ?? 0) - 1)
         return -1;

      const siblings = siblingAccessors[type](upperThing);
      const previousThing = siblings?.[index - 1];
      if (typeof previousThing?.id === "number") return previousThing.id;
      return -1;
   };
   const moveDown = (
      index: number,
      upperThing: MainProduct | Stage | Option,
      type: ThingType
   ): number => {
      if (index === (siblingAccessors[type](upperThing)?.length ?? 0) - 1)
         return -1;
      if (index < 0) return -1;

      const siblings = siblingAccessors[type](upperThing);
      const nextThing = siblings?.[index + 1];
      if (typeof nextThing?.id === "number") return nextThing.id;
      return -1;
   };

   const handleAction = (
      actionType: "moveUp" | "moveDown" | "remove",
      itemType: "stage" | "option" | "item",
      stageIndex?: number,
      optionIndex?: number,
      itemIndex?: number
   ) => {
      console.log(
         "handleAction",
         actionType,
         itemType,
         stageIndex,
         optionIndex,
         itemIndex
      );
      if (!selectedProductId) return;

      if (itemType === "stage") {
         if (stageIndex === undefined) return;
         const currentStageId = product.stages[stageIndex]?.id;
         if (typeof currentStageId !== "number") return;

         if (actionType === "remove") {
            adminStore.deleteStage(selectedProductId, currentStageId);
         } else if (actionType === "moveUp") {
            const previousStageId = moveUp(stageIndex, product, "stage");
            if (previousStageId === -1) return;
            adminStore.reorderStages(
               selectedProductId,
               currentStageId,
               previousStageId
            );
         } else if (actionType === "moveDown") {
            const nextStageId = moveDown(stageIndex, product, "stage");
            console.log("nextStageId", nextStageId);
            if (nextStageId === -1) return;
            adminStore.reorderStages(
               selectedProductId,
               currentStageId,
               nextStageId
            );
         }
      } else if (itemType === "option") {
         if (stageIndex === undefined || optionIndex === undefined) return;
         const stage = product.stages[stageIndex];
         if (!stage || !stage.options) return;
         const currentOptionId = stage.options[optionIndex]?.id;
         if (
            typeof currentOptionId !== "number" ||
            typeof stage.id !== "number"
         )
            return;

         if (actionType === "remove") {
            adminStore.deleteOption(
               selectedProductId,
               stage.id,
               currentOptionId
            );
         } else if (actionType === "moveUp") {
            const previousOptionId = moveUp(optionIndex, stage, "option");
            if (previousOptionId === -1) return;
            adminStore.reorderOptions(
               previousOptionId,
               currentOptionId,
               stage.id,
               selectedProductId
            );
         } else if (actionType === "moveDown") {
            const nextOptionId = moveDown(optionIndex, stage, "option");
            if (nextOptionId === -1) return;
            adminStore.reorderOptions(
               currentOptionId,
               nextOptionId,
               stage.id,
               selectedProductId
            );
         }
      } else if (itemType === "item") {
         if (
            stageIndex === undefined ||
            optionIndex === undefined ||
            itemIndex === undefined
         )
            return;
         const stage = product.stages[stageIndex];
         if (!stage || !stage.options) return;
         const option = stage.options[optionIndex];
         if (!option || !option.items) return;
         const currentItemId = option.items[itemIndex]?.id;
         if (
            typeof currentItemId !== "number" ||
            typeof stage.id !== "number" ||
            typeof option.id !== "number"
         )
            return;

         if (actionType === "remove") {
            adminStore.deleteItem(
               selectedProductId,
               stage.id,
               option.id,
               currentItemId
            );
         } else if (actionType === "moveUp") {
            const previousItemId = moveUp(itemIndex, option, "item");
            if (previousItemId === -1) return;
            adminStore.reorderItems(
               selectedProductId,
               stage.id,
               option.id,
               currentItemId,
               previousItemId
            );
         } else if (actionType === "moveDown") {
            const nextItemId = moveDown(itemIndex, option, "item");
            if (nextItemId === -1) return;
            adminStore.reorderItems(
               selectedProductId,
               stage.id,
               option.id,
               currentItemId,
               nextItemId
            );
         }
      }
   };

   const captureKey = () => {};

   const isItemSelected = (
      type: string,
      stageId?: number,
      optionId?: number,
      itemId?: number
   ) => {
      if (type === "stage") {
         return (
            adminStore.selectedStageId === stageId &&
            adminStore.selectedOptionId === null &&
            adminStore.selectedItemId === null
         );
      }
      if (type === "option") {
         return (
            adminStore.selectedStageId === stageId &&
            adminStore.selectedOptionId === optionId &&
            adminStore.selectedItemId === null
         );
      }
      if (type === "item") {
         return (
            adminStore.selectedStageId === stageId &&
            adminStore.selectedOptionId === optionId &&
            adminStore.selectedItemId === itemId
         );
      }
      return false;
   };

   if (!selectedProductId) return null;

   return (
      <Sidebar className="border-r border-gray-200">
         <SidebarHeader className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">{product.name}</h2>
         </SidebarHeader>

         <SidebarContent className="overflow-auto">
            <SidebarMenu>
               <ConfigurableList
                  items={product.stages}
                  config={stageConfig}
                  expandedState={expandedState}
                  toggleExpand={toggleExpand}
                  level={0}
                  moveUp={(itemType, stageIndex, optionIndex, itemIndex) =>
                     handleAction(
                        "moveUp",
                        itemType,
                        stageIndex,
                        optionIndex,
                        itemIndex
                     )
                  }
                  moveDown={(itemType, stageIndex, optionIndex, itemIndex) =>
                     handleAction(
                        "moveDown",
                        itemType,
                        stageIndex,
                        optionIndex,
                        itemIndex
                     )
                  }
                  remove={(itemType, stageIndex, optionIndex, itemIndex) =>
                     handleAction(
                        "remove",
                        itemType,
                        stageIndex,
                        optionIndex,
                        itemIndex
                     )
                  }
                  isItemSelected={isItemSelected}
               />
            </SidebarMenu>
         </SidebarContent>
      </Sidebar>
   );
}
